import { Head, usePage } from '@inertiajs/react'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid'
import { withAppLayout, PageHeading } from '@/components/layout'
import { Card, CardTitle, Text } from '@/components/ui'
import { useTranslations } from '@/lib/i18n'
import type { SharedProps } from '@/types'

interface GuideProps {
  html: string
  gateway: { dashboard_url: string; docs_url: string }
}

/**
 * In-app user guide. `html` is rendered server-side from docs/USER_GUIDE.md
 * (trusted, in-repo content — not user input) and styled via the `.doc` prose
 * rules in styles/base.css. The resources card surfaces environment-aware links
 * to the gateway's own dashboard + API docs.
 */
function GuideShow({ html, gateway }: GuideProps) {
  const { t } = useTranslations()
  const { props } = usePage<SharedProps>()

  return (
    <>
      <Head title={t('guide.title')} />
      <PageHeading title={t('guide.title')} subtitle={t('guide.subtitle', { brand: props.branding.brand_name })} />

      <Card className="mt-8">
        <CardTitle>{t('guide.resources_title')}</CardTitle>
        <Text className="mt-1">{t('guide.resources_desc')}</Text>
        <div className="mt-4 flex flex-wrap gap-3">
          <GatewayLink href={gateway.dashboard_url}>{t('guide.dashboard_link')}</GatewayLink>
          <GatewayLink href={gateway.docs_url}>{t('guide.api_docs_link')}</GatewayLink>
        </div>
      </Card>

      <Card className="mt-6">
        <div className="doc mx-auto max-w-3xl" dangerouslySetInnerHTML={{ __html: html }} />
      </Card>
    </>
  )
}

// External gateway links open in a new tab (they leave the app).
function GatewayLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-control border border-zinc-950/10 px-3 py-2 text-sm font-medium text-text-strong hover:bg-bg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-turquoise"
    >
      {children}
      <ArrowTopRightOnSquareIcon aria-hidden="true" className="size-4 text-text/50" />
    </a>
  )
}

GuideShow.layout = withAppLayout

export default GuideShow
